import React from 'react';
import { Form, FormSubmit } from '@dashboard/components/Formik';
import usePage from '@dashboard/hooks/usePage';
import { useFeeEdit } from './FeeEditContext';
import {
  feeId,
  feeValidationSchema,
  getFeeFormValues,
  getPageCurrency,
  isFilled,
  PERCENTAGE_OR_AMOUNT,
  paymentMethods,
  ZERO_DECIMAL_CURRENCIES,
} from './feeFormValues';
import useFeeHelpers from './useFeeHelpers';

export default function FeeEdit({ fee }) {
  const { page } = usePage();
  const { setIsEditingFee } = useFeeEdit();

  const id = feeId(fee);
  const { onSaveFee, onDirty, onCancelAddFee } = useFeeHelpers(id);

  const currency = getPageCurrency(page);

  const onCancel = () => {
    if (fee.isNew) {
      onCancelAddFee();
    } else {
      setIsEditingFee(false, id);
    }
  };

  return (
    <Form
      onDirty={onDirty}
      validationSchema={feeValidationSchema}
      validateOnBlur={false}
      validateOnChange
      initialValues={getFeeFormValues(fee, page)}
      onSubmit={async (values, actions) => {
        const { data, error, savedFee } = await onSaveFee(values);

        if (!data) {
          return { error };
        }

        actions.resetForm({ values: getFeeFormValues(savedFee, page) });

        return { data };
      }}
      fields={({ values }) => {
        const isPercentageOnly = isFilled(values.percentage) && !isFilled(values.amount);

        return [
          {
            id: `fees.${id}.name`,
            name: 'name',
            label: 'Fee name',
            placeholder: 'Fee name',
            type: 'text',
            required: true,
            autoFocus: true,
          },
          {
            type: 'columns',
            columns: 2,
            gap: 'sm',
            noMargin: true,
            fields: [
              {
                id: `fees.${id}.percentage`,
                name: 'percentage',
                label: 'Percentage',
                type: 'number',
                placeholder: '0',
                suffix: '%',
              },
              {
                id: `fees.${id}.amount`,
                name: 'amount',
                label: 'Fixed amount',
                type: 'number',
                placeholder: ZERO_DECIMAL_CURRENCIES.includes(currency) ? '0' : '0.00',
                suffix: (currency || '').toUpperCase(),
              },
            ],
          },
          { type: 'custom', name: PERCENTAGE_OR_AMOUNT, renderField: () => null },
          { type: 'divider', name: 'additional-settings-divider' },
          {
            type: 'disclosure',
            label: 'Additional settings',
            compactRow: true,
            nested: true,
            noMargin: true,
            // Open when the saved fee already uses one, so an active setting is never hidden.
            defaultOpen: Boolean(
              fee.multiplyByQuantity || fee.multiplyByTickets || fee.applyToSpecificPaymentMethods,
            ),
            fields: [
              {
                name: 'multiplyByQuantity',
                tooltip:
                  'If your checkout has a quantity field, the fixed amount is charged for each item instead of once per order.',
                label: 'Charge fixed amount per item',
                type: 'checkbox',
                visible: page.type === 'checkout',
                disabled: isPercentageOnly,
                reduceMargin: true,
              },
              {
                name: 'multiplyByTickets',
                label: 'Charge fixed amount per ticket',
                type: 'checkbox',
                visible: page.type === 'event',
                disabled: isPercentageOnly,
                reduceMargin: true,
              },
              {
                name: 'applyToSpecificPaymentMethods',
                label: 'Apply to specific payment methods',
                type: 'checkbox',
              },
              {
                name: 'paymentMethods',
                placeholder: 'Select payment methods',
                type: 'listbox',
                options: paymentMethods,
                multiple: true,
                inset: true,
                visible: values.applyToSpecificPaymentMethods === true,
              },
            ],
          },
        ];
      }}
      formSubmit={({ dirty }) => (
        <FormSubmit align="end">
          <FormSubmit.State visible={!!dirty || !fee._id} />
          {/* Keeps focus in the field: a message shown on blur would push Cancel down mid-click. */}
          <FormSubmit.Cancel onMouseDown={event => event.preventDefault()} onClick={onCancel} />
          <FormSubmit.Button>Save</FormSubmit.Button>
        </FormSubmit>
      )}
    />
  );
}
